<script lang="ts">
	import Flex from 'svelte-flex';
	import Textfield from '@smui/textfield/styled';
	import RangeSlider from 'svelte-range-slider-pips';
	import Select from 'svelte-select';
	import Radio from '@smui/radio/styled';
	import FormField from '@smui/form-field/styled';
	import Checkbox from '@smui/checkbox/styled';
	import Button, { Label } from '@smui/button/styled';
	import { getClient, query } from 'svelte-apollo';
	import { asyncTimeout, MyJSON } from '../../utils';
	import TryAgainBox from '../../components/Kws/TryAgainBox.svelte';
	import { userInfo } from '../../stores';
	import AppContainer from '../../components/AppContainer.svelte';
	import Text from '../../components/Text.svelte';
	import Card from '../../components/Card.svelte';
	import Spinner from '../../components/Spinner.svelte';
	import {
		GET_JOB_OUTPUT_QUERY,
		SUBMIT_TEXT_QUERY,
		SUBMIT_RECOMMENDED_QUERY
	} from '../../graphql/queries/kws';
	import { userClientData } from '../../stores';
	import UrlRow from '../../components/Kws/UrlRow.svelte';
	import RecommendedKeyword from '../../components/Kws/RecommendedKeyword.svelte';

	let isLoading = false;
	let isRecommendedLoading = false;
	let isSent = false;
	let lastRunId = '';
	let retryBox = false;
	let runStatus: string;

	const REFETCH_TIMEOUT = 2000;
	const REFETCH_COUNT = 2;

	let keywordsOpened = true;
	let includedKeywords = '';
	let excludedKeywords = '';
	let recommendedKeywords = [];

	let domainsOpened = true;
	let includedDomains = '';
	let excludedDomains = '';
	let domainsNumber = [100000];
	let domainsGeolocations = [
		{ value: 'cz', label: 'CZ' },
		{ value: 'sk', label: 'SK' },
		{ value: 'com', label: 'COM' },
		{ value: 'at', label: 'AT' },
		{ value: 'de', label: 'DE' }
	];
	let domainsOutput = 1;

	let othersOpened = true;
	let seconds = [0, 60];
	let cpm = [0, 120];
	let ctr = [0, 100];
	let bid = [0, 1.5];
	let formats = [
		'300x250',
		'300x600',
		'500x200',
		'1920x1080',
		'300x300',
		'480x480',
		'480x300',
		'2000x1400',
		'970x210',
		'160x600',
		'336x280',
		'728x90',
		'970x310'
	];
	let devices = ['Mobile', 'Desktop'];

	let domainsNumberMain = 0;
	let domainsNumberAll = 0;
	let domainsNumberPercentage = 0;
	let averageCpm = 0;
	let averageCtr = 0;
	let averageSeconds = 0;

	let urls = [];
	let urlsToRemove = [];

	const getDomainArray = (domainText: string) => {
		let splited = domainText.split('\n');
		splited = splited
			.filter((domain: string) => {
				return domain !== '';
			})
			.map((domain: string) => {
				return domain.trim();
			});
		return splited;
	};

	const client = getClient();
	const submitTextFetch = query(client, {
		query: SUBMIT_TEXT_QUERY,
		variables: {
			inputText: includedKeywords,
			excludedKeywords: excludedKeywords,
			includedDomains: getDomainArray(includedDomains),
			excludedDomains: getDomainArray(excludedDomains),
			domainsNumber: domainsNumber[0],
			domainsGeo: domainsGeolocations.map((geo) => geo.value),
			domainsOutput: domainsOutput,
			secondsMin: seconds[0],
			secondsMax: seconds[1],
			cpmMin: cpm[0],
			cpmMax: cpm[1],
			ctrMin: ctr[0],
			ctrMax: ctr[1],
			formats: formats,
			devices: devices,
			clientLabel: $userInfo.client.label,
			minBid: bid[0],
			maxBid: bid[1]
		},
		errorPolicy: 'all'
	});

	const submitRecommendedFetch = query(client, {
		query: SUBMIT_RECOMMENDED_QUERY,
		variables: {
			inputText: includedKeywords,
			clientLabel: $userInfo.client.label
		},
		errorPolicy: 'all'
	});

	const handleGenerateTargetingLists = async () => {
		if (includedKeywords !== '') {
			lastRunId = '';
			runStatus = 'AI is working';
			retryBox = false;
			isLoading = true;
			urls = [];

			const { data } = await submitTextFetch.refetch({
				inputText: includedKeywords,
				excludedKeywords: excludedKeywords,
				includedDomains: getDomainArray(includedDomains),
				excludedDomains: getDomainArray(excludedDomains),
				domainsNumber: domainsNumber[0],
				domainsGeo: domainsGeolocations.map((geo) => geo.value),
				domainsOutput: domainsOutput,
				secondsMin: seconds[0],
				secondsMax: seconds[1],
				cpmMin: cpm[0],
				cpmMax: cpm[1],
				ctrMin: ctr[0],
				ctrMax: ctr[1],
				formats: formats,
				devices: devices,
				clientLabel: $userInfo.client.label,
				minBid: bid[0],
				maxBid: bid[1]
			});
			const { runId } = data && data.linkifyText;

			if (runId) {
				lastRunId = runId;
				runStatus = 'AI is working';
				await asyncTimeout(REFETCH_TIMEOUT);
				return getJobOutputAction(runId, REFETCH_COUNT);
			}

			isLoading = false;
			runStatus = 'ERROR';
		} else {
			alert('Please fill included keywords first');
		}
	};

	const handleGenerateRecommendedKeywords = async () => {
		if (includedKeywords !== '') {
			isRecommendedLoading = true;
			recommendedKeywords = [];

			const { data } = await submitRecommendedFetch.refetch({
				inputText: includedKeywords,
				clientLabel: $userInfo.client.label
			});
			const { runId } = data && data.recommendedKeywords;

			if (runId) {
				await asyncTimeout(REFETCH_TIMEOUT);
				return getJobOutputActionForRecommended(runId);
			}

			isRecommendedLoading = false;
		} else {
			alert('Please fill included keywords first');
		}
	};

	const getJobOutputAction = async (runId: string, retries: number) => {
		retryBox = false;
		if (runId !== lastRunId) return;
		isLoading = true;

		const { data } = await query(client, {
			query: GET_JOB_OUTPUT_QUERY,
			errorPolicy: 'all'
		}).refetch({ runId });

		const { runStatus: newRunStatus, runOutput, errorText } = data && data.linkifyGetJobOutput;

		runStatus = newRunStatus;

		if (['RUNNING', 'PENDING', 'TERMINATING'].includes(runStatus)) {
			await asyncTimeout(REFETCH_TIMEOUT);
			return getJobOutputAction(runId, retries - 1);
		}

		if (runStatus === 'SUCCESS') {
			console.log(MyJSON.parse(runOutput));
			const {
				WL: newUrls,
				domains_number: newDomainsNumber,
				average_cpm,
				average_ctr,
				average_seconds,
				domains_all,
				percentage_reached_domains
			} = MyJSON.parse(runOutput);
			urls = newUrls;
			isSent = true;
			domainsNumberMain = newDomainsNumber;
			domainsNumberAll = domains_all;
			domainsNumberPercentage = percentage_reached_domains;
			averageCpm = average_cpm;
			averageCtr = average_ctr;
			averageSeconds = average_seconds;
		} else {
			runStatus = `Job run is in invalid status: ${runStatus}. Error: ${errorText}`;
			retryBox = true;
		}

		isLoading = false;
	};

	const getJobOutputActionForRecommended = async (runId: string) => {
		const { data } = await query(client, {
			query: GET_JOB_OUTPUT_QUERY,
			errorPolicy: 'all'
		}).refetch({ runId });

		const { runStatus, runOutput } = data && data.linkifyGetJobOutput;

		if (['RUNNING', 'PENDING', 'TERMINATING'].includes(runStatus)) {
			await asyncTimeout(REFETCH_TIMEOUT);
			return getJobOutputActionForRecommended(runId);
		}

		if (runStatus === 'SUCCESS') {
			let runOutputParsed = MyJSON.parse(runOutput);
      //@ts-ignore
			recommendedKeywords = runOutputParsed.recommended_keywords.filter(n => !excludedKeywords.split(" ").includes(n));
		}

		isRecommendedLoading = false;
	};

	function addToRemoveArray(url: string) {
		const tempArray = urlsToRemove;
		if (!tempArray.includes(url)) {
			tempArray.push(url);
		}
		urlsToRemove = tempArray;
	}

	function removeFromRemoveArray(url: string) {
		const tempArray = urlsToRemove;
		tempArray.splice(tempArray.indexOf(url), 1);
		urlsToRemove = tempArray;
	}

	const downloadCsv = function (content: string, fileName: string, mimeType: string) {
		var a = document.createElement('a');
		mimeType = mimeType || 'application/octet-stream';

		if (navigator.msSaveBlob) {
			//IE
			navigator.msSaveBlob(
				new Blob([content], {
					type: mimeType
				}),
				fileName
			);
		} else if (URL && 'download' in a) {
			a.href = URL.createObjectURL(
				new Blob([content], {
					type: mimeType
				})
			);
			a.setAttribute('download', fileName);
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} else {
			location.href = 'data:application/octet-stream,' + encodeURIComponent(content); // only this mime type is supported
		}
	};

	function generateWhitelist() {
		const finalWhitelist = urls.filter((urlArray) => {
			if (!urlsToRemove.includes(urlArray.url)) {
				return true;
			} else {
				return false;
			}
		});

		const fixedWhitelist = finalWhitelist.map((nArray) => {
			return [nArray.url, nArray.score.toFixed(2)];
		});

		let csvContent = '';
		fixedWhitelist.forEach((infoArray, index) => {
			const dataString = infoArray.join(';');
			csvContent += index < fixedWhitelist.length ? dataString + '\n' : dataString;
		});
		downloadCsv(csvContent, 'whitelist.csv', 'text/csv;encoding:utf-8');
	}

	const addRecommendedToIncluded = (keyword: string) => {
		includedKeywords = includedKeywords + ' ' + keyword;
		recommendedKeywords = recommendedKeywords.filter((e) => e !== keyword);
	};

	const addRecommendedToExcluded = (keyword: string) => {
		excludedKeywords = excludedKeywords + ' ' + keyword;
		recommendedKeywords = recommendedKeywords.filter((e) => e !== keyword);
	};
</script>

<AppContainer>
	<section>
		<Text gutter type="headline4">Keyword targeting</Text>

		{#if isSent}
			<Flex justify="between" style="margin-bottom: 50px; margin-top: 40px">
				<div class="half-item">
					<Flex justify="between">
						<div class="item" style="display: flex; justify-content: space-between;">
							<div>
								<Text type="subtitle1">domains</Text>
								<Text type="headline4">
									{domainsNumberMain.toLocaleString()} / {domainsNumberAll.toLocaleString()}<br />
									<small>({domainsNumberPercentage.toFixed(3)}%)</small>
								</Text>
							</div>
						</div>
					</Flex>
				</div>
				<div class="half-item">
					<Flex justify="end">
						<div class="item" style="display: flex; justify-content: flex-end;">
							<Button variant="raised" on:click={() => generateWhitelist()}>
								<Label>Generate white list</Label>
							</Button>
						</div>
					</Flex>
				</div>
			</Flex>
		{/if}

		<div
			style="cursor: pointer;margin-top: 20px;"
			on:click={() => (keywordsOpened = !keywordsOpened)}
		>
			<Text type="headline6"
				>Keyword settings <span style={'color:' + $userClientData.webData.primaryColor}
					>{@html keywordsOpened ? '&#x25B2;' : '&#x25BC;'}</span
				></Text
			>
		</div>
		{#if keywordsOpened}
			<Card class="card">
				<Flex>
					<div class="half-item">
						<label for="includedKeywords">
							<Text type="subtitle1">Included keywords:</Text>
						</label>
						<Textfield
							class="textarea fullwidth"
							variant="outlined"
							bind:value={includedKeywords}
							textarea
							input$id="includedKeywords"
						/>
					</div>
					<div class="half-item">
						<label for="excludedKeywords">
							<Text type="subtitle1">Excluded keywords:</Text>
						</label>
						<Textfield
							class="textarea fullwidth"
							variant="outlined"
							bind:value={excludedKeywords}
							textarea
							input$id="excludedKeywords"
						/>
					</div>
					<div class="item" style="margin-top: 20px;">
						{#if !isRecommendedLoading}
							<Button variant="raised" type="button" on:click={handleGenerateRecommendedKeywords}>
								<Label>Generate recommended keywords</Label>
							</Button>
							<label for="recommendedKeywords" style="display: block;margin-top: 15px">
								<Text type="subtitle1">Recommended keywords:</Text>
							</label>
							<div class="recommended-wrapper">
								{#if recommendedKeywords.length > 0}
									{#each recommendedKeywords as keyword}
										<RecommendedKeyword
											{keyword}
											on:plus={() => addRecommendedToIncluded(keyword)}
											on:minus={() => addRecommendedToExcluded(keyword)}
										/>
									{/each}
								{/if}
							</div>
						{:else}
							<div class="loading-wrap">
								<Spinner />
							</div>
						{/if}
					</div>
				</Flex>
			</Card>
		{/if}

		<div
			style="cursor: pointer; margin-top: 20px;"
			on:click={() => (domainsOpened = !domainsOpened)}
		>
			<Text type="headline6"
				>Domain settings <span style={'color:' + $userClientData.webData.primaryColor}
					>{@html domainsOpened ? '&#x25B2;' : '&#x25BC;'}</span
				></Text
			>
		</div>
		{#if domainsOpened}
			<Card class="card">
				<Flex align="start">
					<div class="half-item">
						<label for="includedDomains">
							<Text type="subtitle1">Included domains:<br /><br /></Text>
						</label>
						<Textfield
							class="textarea fullwidth"
							variant="outlined"
							bind:value={includedDomains}
							textarea
							input$id="includedDomains"
						/>
					</div>
					<div class="half-item">
						<label for="excludedDomains">
							<Text type="subtitle1"
								>Excluded domains:<br /><em
									>(the default blacklist is already taken into account)</em
								></Text
							>
						</label>
						<Textfield
							class="textarea fullwidth"
							variant="outlined"
							bind:value={excludedDomains}
							textarea
							input$id="excludedDomains"
						/>
					</div>
					<div class="half-item" style="margin-top: 20px;">
						<div class="slider-combo">
							<div>Number of rows in WL:</div>
							<RangeSlider range="min" bind:values={domainsNumber} max={20000} step={1} />
							<input type="number" style="width: 95px" bind:value={domainsNumber[0]} />
						</div>
						<div class="select-combo" style="margin-top: 20px">
							<label for="geolocations">Domain geolocation:</label>
							<Select
								id="geolocations"
								selectedValue={domainsGeolocations}
								multiple
								items={[
									{ value: 'cz', label: 'CZ' },
									{ value: 'sk', label: 'SK' },
									{ value: 'com', label: 'COM' },
									{ value: 'at', label: 'AT' },
									{ value: 'de', label: 'DE' }
								]}
								isMulti
								on:select={(e) => {
									domainsGeolocations = e.detail;
								}}
							/>
						</div>
					</div>
					<div class="half-item" style="margin-top: 20px;">
						<Flex justify="start" align="start" style="padding-left: 15px; padding-right: 15px;">
							<div>Output settings:</div>
							<div>
								<div>
									<FormField>
										<Radio bind:group={domainsOutput} value={1} />
										<span slot="label">Only domain</span>
									</FormField>
								</div>
								<div>
									<FormField>
										<Radio bind:group={domainsOutput} value={2} />
										<span slot="label">Domain and category</span>
									</FormField>
								</div>
								<div>
									<FormField>
										<Radio bind:group={domainsOutput} value={3} />
										<span slot="label">Full url</span>
									</FormField>
								</div>
							</div>
						</Flex>
					</div>
				</Flex>
			</Card>
		{/if}

		<div style="cursor: pointer; margin-top: 20px;" on:click={() => (othersOpened = !othersOpened)}>
			<Text type="headline6"
				>Other settings <span style={'color:' + $userClientData.webData.primaryColor}
					>{@html othersOpened ? '&#x25B2;' : '&#x25BC;'}</span
				></Text
			>
		</div>
		{#if othersOpened}
			<Card class="card">
				<Flex align="start" justify="start">
					<div class="item">
						<div class="slider-combo">
							<div style="margin-right: 10px">Visibility time:</div>
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={seconds[0]} />
								<div class="metric">s</div>
							</div>
							<RangeSlider range bind:values={seconds} max={60} step={1} />
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={seconds[1]} />
								<div class="metric">s</div>
							</div>
						</div>
						<div class="slider-combo">
							<div style="margin-right: 35px">CPM:</div>
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={cpm[0]} />
								<div class="metric">czk</div>
							</div>
							<RangeSlider range bind:values={cpm} max={100} step={1} />
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={cpm[1]} />
								<div class="metric">czk</div>
							</div>
						</div>
						<div class="slider-combo">
							<div style="margin-right: 38px">CTR:</div>
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={ctr[0]} step={0.01} />
								<div class="metric">%</div>
							</div>
							<RangeSlider range bind:values={ctr} max={100} step={0.01} />
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={ctr[1]} step={0.01} />
								<div class="metric">%</div>
							</div>
						</div>
						<div class="slider-combo">
							<div style="margin-right: 48px">Bid:</div>
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={bid[0]} step={0.01} />
							</div>
							<RangeSlider range bind:values={bid} max={1.5} step={0.01} />
							<div class="input-with-metric">
								<input type="number" style="width: 70px" bind:value={bid[1]} step={0.01} />
							</div>
						</div>
					</div>
					<div class="item">
						<Flex justify="start" align="start" style="padding-left: 15px; padding-right: 15px;">
							<div style="margin-top: 15px">Banners:</div>
							<div>
								<FormField>
									<Checkbox bind:group={formats} value="300x250" />
									<span slot="label">300x250</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="300x600" />
									<span slot="label">300x600</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="500x200" />
									<span slot="label">500x200</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="1920x1080" />
									<span slot="label">1920x1080</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="300x300" />
									<span slot="label">300x300</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="480x480" />
									<span slot="label">480x480</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="480x300" />
									<span slot="label">480x300</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="2000x1400" />
									<span slot="label">2000x1400</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="970x210" />
									<span slot="label">970x210</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="160x600" />
									<span slot="label">160x600</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="336x280" />
									<span slot="label">336x280</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="728x90" />
									<span slot="label">728x90</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={formats} value="970x310" />
									<span slot="label">970x310</span>
								</FormField>
							</div>
							<div style="margin-top: 15px; width:100%">Devices:</div>
							<div>
								<FormField>
									<Checkbox bind:group={devices} value="Mobile" />
									<span slot="label">Mobile</span>
								</FormField>
								<FormField>
									<Checkbox bind:group={devices} value="Desktop" />
									<span slot="label">Desktop and Laptop</span>
								</FormField>
							</div>
						</Flex>
					</div>
				</Flex>
			</Card>
		{/if}

		<Flex style="margin-top: 35px; margin-bottom: 35px;">
			<Button variant="raised" on:click={handleGenerateTargetingLists}>
				<Label>Generate targeting list</Label>
			</Button>
		</Flex>

		{#if isLoading}
			<hr style="margin-bottom: 35px;" />
			<div class="loading-wrap">
				<Spinner />
				<Text type="headline4">AI is working hard</Text>
			</div>
		{:else if urls.length > 0}
			<hr style="margin-bottom: 35px;" />
			<Text gutter type="headline4">Sample results URL</Text>

			<div style="overflow-x:auto;">
				<table class="urls-table">
					<thead>
						<tr>
							<td colspan="3" />
							<td>Visibillity time:</td>
							<td>CPM:</td>
							<td>CTR:</td>
							<td />
						</tr>
					</thead>
					<tr class="content">
						<td class="controls" />
						<td />
						<td style="min-width: 300px; max-width: 475px; word-break: break-all;"
							><strong>AVERAGE</strong></td
						>
						<td><strong>{averageSeconds.toFixed(2)}s</strong></td>
						<td>
							<strong>{averageCpm.toFixed(2)} czk</strong>
						</td>
						<td>
							<strong>{averageCtr.toFixed(3)}%</strong>
						</td>
						<td />
					</tr>
					{#each urls as url}
						{#if urlsToRemove.includes(url.url)}
							<UrlRow toRemove={true} on:controls={() => removeFromRemoveArray(url.url)} {url} />
						{:else}
							<UrlRow toRemove={false} on:controls={() => addToRemoveArray(url.url)} {url} />
						{/if}
					{/each}
					<tr class="divider">
						<td colspan="7" />
					</tr>
				</table>
			</div>
		{:else if isSent}
			<hr style="margin-bottom: 35px;" />
			<div>No URLs were found for you settings</div>
		{/if}
		{#if retryBox}
			<TryAgainBox
				runId={lastRunId}
				{runStatus}
				on:click={() => getJobOutputAction(lastRunId, REFETCH_COUNT)}
			/>
		{/if}
	</section>
</AppContainer>

<style>
	:root {
		--range-slider: #dddcdd;
		--range-handle-inactive: #0e3267; /* inactive handle color */
		--range-handle: #0e3267; /* non-focussed handle color */
		--range-handle-focus: #0e3267;
	}

	:global(.rangeSlider > .rangeHandle) {
		outline: none !important;
		z-index: 1 !important;
	}

	:global(.rangeSlider) {
		flex: 1 1 auto;
	}

	.item,
	.half-item {
		width: 100%;
		flex: 0 0 100%;
	}

	:global(.card) {
		background-color: #f2f2f2;
	}

	:global(.textarea) {
		background-color: white !important;
		border: 0;
		width: 100%;
	}

	:global(.textarea textarea) {
		resize: none;
		height: 145px !important;
	}

	:global(.flex) {
		margin-left: -15px;
		margin-right: -15px;
		flex-wrap: wrap;
	}

	:global(.flex .item, .flex .half-item) {
		padding-left: 15px;
		padding-right: 15px;
	}

	.slider-combo {
		display: flex;
		align-items: center;
	}

	.slider-combo input[type='number'] {
		font-size: 15px;
		height: 35px;
	}

	.select-combo {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.input-with-metric {
		position: relative;
	}

	.input-with-metric .metric {
		position: absolute;
		right: 5px;
		top: 50%;
		transform: translateY(-50%);
	}

	.input-with-metric input {
		padding-right: 20px;
	}

	.urls-table {
		border-collapse: collapse;
		max-width: calc(100% - 2px);
		min-width: calc(100% - 2px);
	}

	:global(.urls-table td) {
		padding-left: 15px;
		padding-right: 15px;
		padding-top: 5px;
		padding-bottom: 5px;
	}

	:global(.urls-table tr.divider td) {
		height: 10px;
	}

	:global(.urls-table tr.content) {
		background-color: white;
		border: 1px solid #ebeef7;
	}

	:global(tbody) {
		background-color: white;
		border: 1px solid #ebeef7;
	}

	.urls-table thead tr td {
		color: var(--range-handle-inactive);
		font-weight: 600;
	}

	.loading-wrap {
		text-align: center;
	}

	.recommended-wrapper {
		min-height: 175px;
		padding: 10px;
		background-color: white;
		display: flex;
		flex-wrap: wrap;
		align-content: flex-start;
		margin-bottom: 15px;
	}

	@media screen and (min-width: 768px) {
		.half-item {
			width: 50%;
			flex: 0 0 50%;
		}
	}
</style>
